import { useState, FC, useEffect } from 'react';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { css } from '@emotion/react';
import { Capacitor } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { useTranslation } from 'react-i18next';
import { discordInviteUrl } from '../../constants';
import { PushNotificationService } from '../../services/pushNotificationService';
import { registerDevice } from '../../apis/backend';
import * as stores from '../../stores';
import { usePlaidFeatureFlag } from '../../hooks/useRemoteConfig';
import { reportError } from '../../utils/reporting';
import Page from '../Page';
import { colors, spacing, fontStyles } from '../../styles';
import StickyHeader from '../StickyHeader';
import NotificationBadge from '../NotificationBadge/NotificationBadge';
import { BellIcon } from '../Icons';
import FeedbackTakeover from '../FeedbackTakeover';
import share from '../../utils/share';
import { getInitialTheme, setTheme } from '../../utils/themeManager';
import type { Theme } from '../../utils/themeManager';
import { getInitialFontSize, setFontSize } from '../../utils/fontSizeManager';
import type { FontSize } from '../../utils/fontSizeManager';
import { InvestorType } from '../../utils/smartChatPrompts';
import { getChatModelPreference, setChatModelPreference } from '../../utils/chatModelPreference';
import RowButton from './RowButton';
import ThemeSelector from './ThemeSelector';
import FontSizeSelector from './FontSizeSelector';
import LanguageSelector from './LanguageSelector';
import SubscriptionCard from './SubscriptionCard';
import ManageSubscriptionSheet from './ManageSubscriptionSheet';
import { InvestorPersonaSelector, getInvestorPersona, setInvestorPersona } from './InvestorPersonaSelector';
import { ChatModelSelector } from './ChatModelSelector';
import InlineSettingRow from './InlineSettingRow';
import arrowForward from './arrowForward.svg';

// Utility functions
const isWebPlatform = () => Capacitor.getPlatform() === 'web';
const isAndroidPlatform = () => Capacitor.getPlatform() === 'android';

const getBundleVersionWithFallback = async (nativeVersion: string): Promise<string> => {
  try {
    const bundleInfo = await CapacitorUpdater.current();
    if (bundleInfo && bundleInfo.bundle && bundleInfo.bundle.version) {
      return bundleInfo.bundle.version;
    } else {
      // Fall back to native version if no bundle version available
      return nativeVersion;
    }
  } catch (bundleError) {
    reportError(new Error(`Error getting bundle info: ${bundleError}`));
    // Fall back to native version if bundle info fails
    return nativeVersion;
  }
};

const MorePage: FC = () => {
  const history = useHistory();
  const isPlaidEnabled = usePlaidFeatureFlag();
  const [isFeedbackTakeoverShown, setIsFeedbackTakeoverShown] = useState(false);
  const [isManageSubscriptionSheetOpen, setIsManageSubscriptionSheetOpen] = useState(false);
  const restoreMutation = useMutation(() => Purchases.restorePurchases());
  const isPurchaseInfoLoaded = stores.Purchase.useState((s) => s.isPurchaseInfoLoaded);
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isSubscribed = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => getInitialTheme());
  const [currentFontSize, setCurrentFontSize] = useState<FontSize>(() => getInitialFontSize());
  const [currentPersona, setCurrentPersona] = useState<InvestorType | null>(() => getInvestorPersona());
  const [currentChatModel, setCurrentChatModel] = useState<string | null>(() => getChatModelPreference());
  const { t, i18n } = useTranslation();
  // TODO: Extend when adding languages beyond es/en (see also pushNotificationService.ts and notification_translator.py)
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'es'>(i18n.language.startsWith('es') ? 'es' : 'en');
  const [hasNotificationPermission, setHasNotificationPermission] = useState<boolean>(false);
  const [appBuild, setAppBuild] = useState<string>('');
  const [bundleVersion, setBundleVersion] = useState<string>('');

  useEffect(() => {
    const checkPermissionStatus = async () => {
      try {
        if (isWebPlatform()) {
          // For web, use the standard Notification.permission
          setHasNotificationPermission(Notification.permission === 'granted');
        } else if (Capacitor.isPluginAvailable('FirebaseMessaging')) {
          // For native platforms, use FirebaseMessaging
          // Only consider 'granted' as having permission - 'prompt' means we haven't asked yet
          const { receive } = await FirebaseMessaging.checkPermissions();
          setHasNotificationPermission(receive === 'granted');
        } else {
          // FirebaseMessaging plugin not available
          setHasNotificationPermission(false);
        }
      } catch (error) {
        reportError(new Error(`Error checking notification permissions: ${error}`));
        setHasNotificationPermission(false);
      }
    };

    // Check initial permission status when component mounts
    checkPermissionStatus();
  }, []);

  useEffect(() => {
    const getAppInfo = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          const appInfo = await App.getInfo();
          setAppBuild(appInfo.build);

          // Get the current bundle version with fallback
          const version = await getBundleVersionWithFallback(appInfo.version);
          setBundleVersion(version);
        }
      } catch (error) {
        reportError(new Error(`Error getting app info: ${error}`));
        setAppBuild('Unknown');
        setBundleVersion('Unknown');
      }
    };

    getAppInfo();
  }, []);

  useEffect(() => {
    let listener: any;

    const setupListener = async () => {
      const checkPermissionOnResume = async () => {
        try {
          if (isWebPlatform()) {
            // For web, use the standard Notification.permission
            setHasNotificationPermission(Notification.permission === 'granted');
          } else if (Capacitor.isPluginAvailable('FirebaseMessaging')) {
            // For native platforms, use FirebaseMessaging
            const { receive } = await FirebaseMessaging.checkPermissions();
            setHasNotificationPermission(receive === 'granted');
          } else {
            // FirebaseMessaging plugin not available
            setHasNotificationPermission(false);
          }
        } catch (error) {
          reportError(new Error(`Error checking notification permissions on resume: ${error}`));
          setHasNotificationPermission(false);
        }
      };

      // For native platforms, set up the app state change listener
      if (Capacitor.isNativePlatform()) {
        listener = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            checkPermissionOnResume();
          }
        });
      }
    };

    setupListener();

    return () => {
      // Cleanup listener when component unmounts
      if (listener) {
        listener.remove();
      }
    };
  }, []);

  const openNotificationSettings = async () => {
    // Check if we're on web platform
    if (isWebPlatform()) {
      // On web, we can't open device settings, so show instructions
      alert(t('settings.notificationBrowserInstructions'));

      // After user sees the instructions, check if they've enabled permissions
      setTimeout(() => {
        setHasNotificationPermission(Notification.permission === 'granted');
      }, 1000);
      return;
    }

    try {
      // For native platforms, first check if we can request permission directly
      if (Capacitor.isPluginAvailable('FirebaseMessaging')) {
        const { receive } = await FirebaseMessaging.checkPermissions();

        // If permission is 'prompt' (never asked), request it directly instead of opening settings
        // iOS won't show notification settings until we've asked for permission at least once
        if (receive === 'prompt') {
          const result = await FirebaseMessaging.requestPermissions();
          if (result.receive === 'granted') {
            setHasNotificationPermission(true);
            // Navigate to customize page now that permission is granted
            history.push('/notification-customize');
            return;
          }
          // If denied, fall through to open settings
        }

        // If already granted (shouldn't happen since button only shows when not granted)
        if (receive === 'granted') {
          setHasNotificationPermission(true);
          history.push('/notification-customize');
          return;
        }
      }

      // Permission was previously denied - open device settings so user can enable manually
      // Note: NativeSettings.open() returns immediately, it doesn't wait for the user to return.
      // The appStateChange listener (line ~148) handles rechecking permissions when the app resumes.
      await NativeSettings.open({
        optionAndroid: AndroidSettings.AppNotification,
        optionIOS: IOSSettings.AppNotification,
      });
    } catch (error) {
      reportError(new Error(`Error opening settings: ${error}`));
      // Fallback: try to open general settings
      try {
        await NativeSettings.open({
          optionAndroid: AndroidSettings.ApplicationDetails,
          optionIOS: IOSSettings.App,
        });
      } catch (fallbackError) {
        reportError(new Error(`Error opening general settings: ${fallbackError}`));
        alert(t('settings.openSettingsManually'));
      }
    }
  };

  const styles = {
    darkmodeStyle: css`
      html[data-theme='dark'] & {
        background-color: ${colors.liquorice};
        color: ${colors.sugar};
      }
    `,
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // For web platform, add a periodic check for permission changes
    if (isWebPlatform()) {
      const checkPermissionsPeriodically = () => {
        setHasNotificationPermission(Notification.permission === 'granted');
      };

      // Check every 2 seconds to detect permission changes
      const interval = setInterval(checkPermissionsPeriodically, 2000);

      // Also listen for visibility change to check permissions when user returns to tab
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          checkPermissionsPeriodically();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
    // Return undefined for non-web platforms
    return undefined;
  }, []);

  const handleRestartOnboarding = () => {
    localStorage.removeItem('hasCompletedOnboarding');
    history.push('/welcome');
  };

  const handleThemeChange = (newTheme: Theme) => {
    setCurrentTheme(newTheme);
    setTheme(newTheme);
  };

  const handleFontSizeChange = (newSize: FontSize) => {
    setCurrentFontSize(newSize);
    setFontSize(newSize);
  };

  const handlePersonaChange = (newPersona: InvestorType) => {
    setCurrentPersona(newPersona);
    setInvestorPersona(newPersona);
  };

  const handleChatModelChange = (newModel: string) => {
    setCurrentChatModel(newModel === 'default' ? null : newModel);
    setChatModelPreference(newModel);
  };

  const handleNotificationCustomize = () => {
    history.push('/notification-customize');
  };

  const handleBrokerageAction = () => {
    if (!isPurchaseInfoLoaded) return;
    if (!isSubscribed) {
      stores.showPaymentModalByPreference('plaid_brokerage_settings');
      return;
    }
    history.push('/settings/brokerage');
  };

  const handleLanguageChange = async (lang: 'en' | 'es') => {
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);

    // Sync language to backend for notification translation
    try {
      const token = PushNotificationService.getCurrentToken() || (await PushNotificationService.getToken());
      if (token) {
        const savedPreferences = localStorage.getItem('notificationPreferences');

        let notificationTypes: string[] | undefined;
        let watchlistSymbols: string[] | undefined;

        if (savedPreferences) {
          const preferences = JSON.parse(savedPreferences);
          notificationTypes = preferences
            .filter((pref: { enabled: boolean }) => pref.enabled)
            .map((pref: { id: string }) => pref.id);

          const { getPortfolios } = await import('../../storage');
          const portfolios = await getPortfolios();
          watchlistSymbols = Array.from(new Set(portfolios.flatMap((p: any) => p.symbols || []))) as string[];
        }

        await registerDevice(token, Capacitor.getPlatform(), {
          ...(notificationTypes !== undefined && { notificationTypes }),
          ...(watchlistSymbols !== undefined && { watchlistSymbols }),
          language: lang,
        });
      }
    } catch (error) {
      console.error('[LANGUAGE-SYNC] Failed to sync language to backend:', error);
    }
  };

  return (
    <Page showNavigationBar>
      <FeedbackTakeover isShown={isFeedbackTakeoverShown} onClose={() => setIsFeedbackTakeoverShown(false)} />
      <ManageSubscriptionSheet
        isOpen={isManageSubscriptionSheetOpen}
        onClose={() => setIsManageSubscriptionSheetOpen(false)}
      />

      <StickyHeader
        headerCss={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <h1
          css={css`
            color: ${colors.liquorice};
            ${fontStyles.shark};
            ${styles.darkmodeStyle}
            margin: 0;
          `}
        >
          {t('settings.title')}
        </h1>
        <button
          onClick={() => history.push('/notifications')}
          css={css`
            background: none;
            border: none;
            padding: ${spacing.centi};
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${colors.liquorice};

            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }

            &:hover {
              opacity: 0.7;
            }
          `}
          aria-label={t('settings.notificationHistory')}
        >
          <NotificationBadge>
            <BellIcon size={22} />
          </NotificationBadge>
        </button>
      </StickyHeader>

      <SubscriptionCard
        onManageSubscription={() => setIsManageSubscriptionSheetOpen(true)}
        onSeePlans={() => stores.showPaymentModalByPreference('more_page')}
      />

      <div
        css={css`
          margin-top: ${spacing.regular};
        `}
      >
        <h2
          css={css`
            color: ${colors.liquorice};
            margin: 0 0 ${spacing.centi} 0;
            ${fontStyles.lion};
            ${styles.darkmodeStyle}
          `}
        >
          {t('settings.appearance')}
        </h2>
        <InlineSettingRow label={t('settings.theme')}>
          <ThemeSelector selectedTheme={currentTheme} onChange={handleThemeChange} />
        </InlineSettingRow>
        <InlineSettingRow label={t('settings.textSize')}>
          <FontSizeSelector selectedSize={currentFontSize} onChange={handleFontSizeChange} />
        </InlineSettingRow>
        <InlineSettingRow label={t('settings.language')}>
          <LanguageSelector selectedLanguage={currentLanguage} onChange={handleLanguageChange} />
        </InlineSettingRow>
        <h2
          css={css`
            color: ${colors.liquorice};
            margin: ${spacing.regular} 0 ${spacing.centi} 0;
            ${fontStyles.lion};
            ${styles.darkmodeStyle}
          `}
        >
          {t('settings.chat')}
        </h2>
        <InlineSettingRow label={t('settings.aiPersonality')}>
          <InvestorPersonaSelector selectedPersona={currentPersona} onChange={handlePersonaChange} />
        </InlineSettingRow>
        <InlineSettingRow label={t('settings.aiModel')} badge="PRO">
          <ChatModelSelector
            selectedModel={currentChatModel}
            onChange={handleChatModelChange}
            isSubscribed={!!isSubscribed}
          />
        </InlineSettingRow>
      </div>

      {isPlaidEnabled && (
        <div>
          <h2
            css={css`
              color: ${colors.liquorice};
              margin: ${spacing.regular} 0 ${spacing.centi} 0;
              ${fontStyles.lion};
              ${styles.darkmodeStyle}
            `}
          >
            {t('settings.brokerage')}
          </h2>
          <div
            role="button"
            tabIndex={0}
            css={css`
              display: block;
              width: 100%;
              background-color: transparent;
              border: none;
              padding: ${spacing.centi} 0;
              text-align: left;
              ${fontStyles.cat}
              color: ${colors.petrolBlue};
              html[data-theme='dark'] & {
                background-color: ${colors.liquorice};
                color: ${colors.skyBlue};
              }
              &:hover {
                cursor: pointer;
                text-decoration: underline;
              }
            `}
            onClick={handleBrokerageAction}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBrokerageAction();
              }
            }}
          >
            <span>{t('settings.connectBrokerage')}</span>
            <img
              src={arrowForward}
              alt=""
              css={css`
                vertical-align: middle;
                float: right;
              `}
            />
          </div>
        </div>
      )}
      <h2
        css={css`
          color: ${colors.liquorice};
          margin: ${spacing.regular} 0 ${spacing.centi} 0;
          ${fontStyles.lion};
          ${styles.darkmodeStyle}
        `}
      >
        {t('settings.notifications')}
      </h2>
      {hasNotificationPermission ? (
        <div
          role="button"
          tabIndex={0}
          css={css`
            display: block;
            width: 100%;
            background-color: transparent;
            border: none;
            padding: ${spacing.centi} 0;
            text-align: left;
            ${fontStyles.cat}
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              background-color: ${colors.liquorice};
              color: ${colors.skyBlue};
            }
            &:hover {
              cursor: pointer;
              text-decoration: underline;
            }
          `}
          onClick={handleNotificationCustomize}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNotificationCustomize();
            }
          }}
        >
          <span>{t('settings.customizeAlerts')}</span>
          <img
            src={arrowForward}
            alt=""
            css={css`
              vertical-align: middle;
              float: right;
            `}
          />
        </div>
      ) : (
        <button
          onClick={openNotificationSettings}
          css={css`
            display: block;
            width: 100%;
            background: none;
            border: none;
            padding: ${spacing.centi} 0;
            text-align: left;
            ${fontStyles.cat};
            color: ${colors.petrolBlue};
            cursor: pointer;
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {t('settings.enableNotifications')}
        </button>
      )}

      <h2
        css={css`
          color: ${colors.liquorice};
          margin: ${spacing.regular} 0 ${spacing.centi} 0;
          ${fontStyles.lion};
          ${styles.darkmodeStyle}
        `}
      >
        {t('settings.community')}
      </h2>
      {isSubscribed && (
        <>
          <a
            href="https://chat.whatsapp.com/F0m0yPWeh4XFu8iAv6Vmrx"
            css={css`
              text-decoration: none;
            `}
          >
            <RowButton hideChevron>{t('settings.joinWhatsApp')}</RowButton>
          </a>
          <a
            href={discordInviteUrl}
            css={css`
              text-decoration: none;
            `}
          >
            <RowButton hideChevron>{t('settings.joinDiscord')}</RowButton>
          </a>
        </>
      )}
      <a
        href="https://x.com/investwithbloom"
        css={css`
          text-decoration: none;
        `}
      >
        <RowButton hideChevron>{t('settings.followTwitter')}</RowButton>
      </a>
      <RowButton hideChevron onClick={share}>
        {t('settings.shareApp')}
      </RowButton>

      <h2
        css={css`
          color: ${colors.liquorice};
          margin: ${spacing.regular} 0 ${spacing.centi} 0;
          ${fontStyles.lion};
          ${styles.darkmodeStyle}
        `}
      >
        {t('settings.feedback')}
      </h2>

      <RowButton onClick={() => setIsFeedbackTakeoverShown(true)}>{t('settings.sendFeedback')}</RowButton>
      <RowButton
        hideChevron
        onClick={() =>
          window.open(
            isAndroidPlatform()
              ? 'https://play.google.com/store/apps/details?id=com.bloom.invest'
              : 'https://apps.apple.com/us/app/bloom-stock-market-research/id1436348671',
            '_blank',
          )
        }
      >
        {isAndroidPlatform() ? t('settings.rateGooglePlay') : t('settings.rateAppStore')}
      </RowButton>

      <h2
        css={css`
          color: ${colors.liquorice};
          margin: ${spacing.regular} 0 ${spacing.centi} 0;
          ${fontStyles.lion};
          ${styles.darkmodeStyle}
        `}
      >
        {t('settings.legal')}
      </h2>
      <RowButton
        hideChevron
        onClick={() =>
          window.open(
            'https://cliff-ornament-596.notion.site/Bloom-Disclaimer-44a8d3a0a4dd4e718dbff991eba5d2b9',
            '_blank',
          )
        }
      >
        {t('settings.disclaimer')}
      </RowButton>
      <RowButton
        hideChevron
        onClick={() =>
          window.open('https://www.notion.so/Bloom-Terms-and-Conditions-b41a2b80c0b14429801adf810856b1d3', '_blank')
        }
      >
        {t('settings.terms')}
      </RowButton>
      <RowButton
        hideChevron
        onClick={() =>
          window.open('https://www.notion.so/Bloom-Privacy-Policy-0c94935f49344ce4957fe459f4cf128d', '_blank')
        }
      >
        {t('settings.privacy')}
      </RowButton>

      {/* Debug section - only visible to developers */}
      <h2
        css={css`
          color: ${colors.liquorice};
          margin: ${spacing.regular} 0 ${spacing.centi} 0;
          ${fontStyles.lion};
          ${styles.darkmodeStyle}
        `}
      >
        {t('settings.debug')}
      </h2>

      <RowButton
        hideChevron
        disabled={restoreMutation.isLoading}
        onClick={() => {
          (async () => {
            try {
              const { customerInfo } = await restoreMutation.mutateAsync();
              stores.Purchase.update((s) => {
                s.purchaserInfo = customerInfo;
              });
              toast(t('settings.purchaseRestored'));
            } catch (error) {
              console.error('Failed to restore purchases:', error);
              toast(t('settings.purchaseNotFound'));
            }
          })();
        }}
      >
        {restoreMutation.isLoading ? t('settings.restoring') : t('settings.restorePurchase')}
      </RowButton>
      <RowButton onClick={handleRestartOnboarding}>{t('settings.restartOnboarding')}</RowButton>

      {/* Subtle app version info */}
      {bundleVersion && appBuild && (
        <p
          css={css`
            margin: ${spacing.regular} 0 0 0;
            color: ${colors.silver};
            ${fontStyles.thinFlea};
            text-align: center;
            ${styles.darkmodeStyle}
          `}
        >
          v{bundleVersion} ({appBuild})
        </p>
      )}
    </Page>
  );
};

export default MorePage;
